import { Dispatch, SetStateAction, createContext, useEffect, useState } from "react";
import { i18n, Messages } from '@lingui/core';
import { I18nProvider } from '@lingui/react'
import { loadCatalog } from "@/components/catalogloader";

interface IntlProviderProps {
    params: {
        lang: string;
    },
    children: React.ReactNode;
}

export function activateLingui (locale: string, messages: Messages) {
    const isClient = typeof window !== 'undefined'

    if ( !isClient && locale !== i18n.locale ) {
      // there is single instance of i18n on the server
      // note: on the server, we could have an instance of i18n per supported locale
      // to avoid calling loadAndActivate for (worst case) each request, but right now that's what we do
      i18n.loadAndActivate({ locale, messages })
    }

    if ( isClient && ( ( i18n.locale === undefined ) || ( i18n.locale !== locale ) ) ) {
      // first client render
      i18n.loadAndActivate({ locale, messages });
    }

    return( i18n );
}

export const LocaleContext = createContext<[string, Dispatch<SetStateAction<string>>]>(["", () => {}]);

export default function IntlProvider ({ params, children }: IntlProviderProps) {

    const [locale, setLocale] = useState<string>( params.lang );
    const [intlKey, setIntlKey] = useState<string>( params.lang );
    const [loadedLocale, setLoadedLocale] = useState<string>( "" );

    useEffect( () => {
        async function load (locale: string) {
            const messages: Messages = await loadCatalog( locale ) as Messages;
            activateLingui( locale, messages );
            setLoadedLocale( locale );
        }
        load( locale ).then(() => {}).catch(() => {});
    }, [locale]);

    if ( locale !== intlKey && loadedLocale === locale ) {
        // NOTENOTE: setTimeout use is a workaround for React Aria issue
        //           https://github.com/adobe/react-spectrum/issues/4514
        setTimeout( () => setIntlKey( locale ), 500 );
    }

    return(
        <LocaleContext.Provider key={intlKey} value={[locale, setLocale]}>
            <I18nProvider key={intlKey} i18n={i18n}>
                {children}
            </I18nProvider>
        </LocaleContext.Provider>
    );
}